<template>
  <section id="text-modal" class="text-modal">
    <!-- ヘッダー -->
    <div class="modal-header">
      <div v-if="isSP">
        <div
          ref="headerLogo"
          id="header-logo"
          class="header-logo"
          @click="clickTransitionHeaderButton(transitionType.Top)"
        >
          <img
            class="icon logo"
            src="~/assets/pages/common/LogoAmahitotsu_Color_H.svg"
            :alt="$t('util.title')"
          />
        </div>
      </div>
      <div v-else class="svg-icon"></div>
      <!-- タイトル・サブタイトル -->
      <div class="text-content">
        <h1 class="title">{{ textModalData.textModal.title }}</h1>
        <div class="sub-title">{{ textModalData.textModal.subTitle }}</div>
      </div>
    </div>

    <!-- 本文 -->
    <div class="modal-body">
      <div class="scroll-wrapper">
        <div id="text-list-content" class="text-list-content scroll-custom"></div>
      </div>
    </div>

    <!-- フッター -->
    <div class="modal-footer">
      <button class="button close-button" @click="onClickClose">
        <div class="text">
          {{ closeButtonText }}
        </div>
      </button>
      <button
        v-if="isAcceptButtonActive"
        id="accept-button"
        class="button accept-button"
        @click="onClickAccept"
      >
        <div class="text">
          {{ acceptButtonText }}
        </div>
      </button>
    </div>
  </section>
</template>
<script setup lang="ts">
const { t: $t } = useI18n()

import { useMounted, useWindowSize } from "@vueuse/core";
import { getTextModal } from '~~/src/models/useTextModal'
import {
  actionTransition,
  type actionTransitionType,
  transitionType,
} from '@/models/Utility'
const { locale } = useI18n()
const textModalData = computed(() => {
  return getTextModal().value
})

// 閉じるボタン
const onClickClose = () => {
  if (textModalData.value.close) {
    textModalData.value.close()
  }
  textModalData.value.show = false
  setShowTextModal(false)
}

// 送信するボタン
const onClickAccept = () => {
  if (textModalData.value.accept) {
    textModalData.value.accept()
  }
  textModalData.value.show = false
  setShowTextModal(false)
}

watch(getTextModal(), () => {
  setShowTextModal(textModalData.value.show)
  createTextList()
  setButtonList()
})

const setShowTextModal = (state: boolean) => {
  const textModal = document.getElementById('text-modal')
  if (!textModal) return

  if (state) {
    textModal.classList.add('active')
  } else {
    textModal.classList.remove('active')
  }
}

// ボタンセットアップ
const isAcceptButtonActive = ref(false)

const closeButtonText = ref('')
const acceptButtonText = ref('')

const setButtonList = () => {
  const customButton = textModalData.value.textModal.customButton
  if (!customButton) return

  const closeButtonCustom = customButton.closeButton
  if (closeButtonCustom) {
    closeButtonText.value = closeButtonCustom.title
  } else {
    closeButtonText.value = $t('modal.close-button')
  }
  isAcceptButtonActive.value = false
  const acceptButtonCustom = customButton.acceptButton
  if (acceptButtonCustom) {
    acceptButtonText.value = acceptButtonCustom.title
    isAcceptButtonActive.value = true
  }
}

// 本文セットアップ
const createTextList = () => {
  const textListContent = document.getElementById('text-list-content')
  if (!textListContent) return

  const deleteChildren: ChildNode[] = []
  textListContent.childNodes.forEach((value) => {
    deleteChildren.push(value)
  })
  deleteChildren.forEach((value) => {
    textListContent.removeChild(value)
  })

  textListContent.scroll({ top: 0 })

  if (textModalData.value.textModal.prefaceText) {
    const prefaceText = document.createElement('div')
    prefaceText.className = 'preface-text'
    prefaceText.innerHTML = textModalData.value.textModal.prefaceText
    textListContent.appendChild(prefaceText)
  }

  textModalData.value.textModal.textList.forEach((value) => {
    // タイトルが空の場合はスキップ（英語のフリガナ入力欄が該当）
    if (!value.headerTitle) {
      return
    }
    const headerText = document.createElement('div')
    headerText.className = 'header-text'
    headerText.innerText = value.headerTitle
    textListContent.appendChild(headerText)
    const headerDetail = document.createElement('ul')
    headerDetail.className = 'header-detail'
    value.headerDetailList.forEach((detail) => {
      const headerDetailText = document.createElement('li')
      headerDetailText.className = 'header-detail-text'
      // 基本はHTMLとして表示するが、HTMLを無効化したい場合はisHtmlをfalseにする
      if (value.isHtml === false) {
        headerDetailText.innerText = detail
      } else {
        headerDetailText.innerHTML = detail
      }
      headerDetail.appendChild(headerDetailText)
    })
    textListContent.appendChild(headerDetail)
  })

  if (textModalData.value.textModal.lastUpdatedText) {
    const lastUpdatedText = document.createElement('div')
    lastUpdatedText.className = 'last-updated-text'
    lastUpdatedText.innerHTML = textModalData.value.textModal.lastUpdatedText
    textListContent.appendChild(lastUpdatedText)
  }

  if (textModalData.value.textModal.textContentBorderActive) {
    textListContent.classList.add('border')
  } else {
    textListContent.classList.remove('border')
  }
}

const isMounted = useMounted()
// SP表示かどうか
const { width } = useWindowSize()
const isSP = computed(() => {
  if (!isMounted.value) return true
  return width.value <= 767
});

const localePath = useLocalePath()
const clickTransitionHeaderButton = (transition: actionTransitionType) => {
  actionTransition(localePath(transition))
  if (isSP.value) {
    onClickClose();
  }
}
</script>
<style lang="scss" scoped>
@use '@/assets/styles/mixins' as m;
@use '@/assets/styles/variables' as v;
@use '@/assets/styles/util' as u;

.text-modal {
/*   justify-content: center; */
  align-items: center;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: auto;
  z-index: v.$zindex-dialog;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  opacity: 0;
  transition: all 0.3s;
  visibility: hidden;

  @include m.sp() {
    bottom: initial;
  }

  &.active {
    opacity: 1;
    visibility: visible;
  }

  /* ヘッダー */
  .modal-header {
    display: flex;
    width: 100%;
    min-height: 144px;
    place-items: center;
    background: v.$bg-white-color;
    position:relative;
    @include m.sp() {
      height: 120px;
      display: block;

      /* ヘッダーロゴ */
      .header-logo {
        position: absolute;
        top: 8px;
        left: 16px;
        width: 17vw;
      }
    }

    /* アイコン */
    .svg-icon {
      position: absolute;
      width: 88px;
      height: 96px;
      background-image: url('~/assets/pages/common/Logo_Collar.svg');
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
      display: block;
      -webkit-mask-image: none;
      mask-image: none;
      left: max(2vw, 1vw + (50vw - 584px));
    }
    .text-content {
      flex: 4;
      text-align: center;
      display: flex;
      flex-direction: column;

      @include m.sp() {
        flex: initial;
        margin-top: 34px;
      }

      /* タイトル */
      .title {
        color: v.$accent-color;
        font-size: 1.125rem;
        font-family: v.$accent-font-family;
        font-weight: v.$font-weight-light;
        margin: 0;
        @include m.sp() {
          font-size: 1rem;
          letter-spacing: 6.4px;
        }
      }

      /* サブタイトル */
      .sub-title {
        color: v.$common-element-color;
        font-size: 1.5rem;
        max-width: 70%;
        text-align: center;
        margin: 0 auto;
        line-height: 1.5;
        @include m.sp() {
          font-size: 0.875rem;
          letter-spacing: 2.1px;
          max-width: 100%;
        }
      }
    }
  }

  /* 本文 */
  .modal-body {
    flex: 1;
    background: v.$common-white-color;
    width: 100%;
    height: 624px;
    padding: 0 1vw;
    align-content: center;
    @include m.sp() {
      background: v.$common-white-color;
      height: 300px;
      padding: 0;
    }

    /* テキストリストのラッパー */
    :deep(.scroll-wrapper) {
      box-sizing: border-box;
      max-width: 1168px;
      height: 95%;
      position: relative;
      background: v.$bg-white-color;
      display: flex;
      flex-direction: column;
      padding:8px 24px 8px 0;
      margin: 32px auto;
      overflow: hidden;
      @include m.sp() {
        padding: 1px;
        margin: 10px;
      }
    }

    /* テキストリスト（見出し＋詳細のリスト） */
    :deep(.text-list-content) {
      width: 100%;
      flex-grow: 1;
      height: 0;
      margin: 20px 10px;
      padding: 1% 2%;
      overflow-y: auto;
      @include m.sp() {
        padding: 8px 16px 8px 8px;
        margin: 8px 4px;
      }

      &.border {
        border: none;
        @include m.sp() {
          border: none;
        }
      }

      @include m.sp() {
        width: 95%;
      }

      /* 見出しテキスト */
      .header-text {
        text-align: start;
        width: 100%;
        font-size: 0.875rem;
        background: v.$bg-list-color;
        padding: 13px 0 12px 18px;
        @include m.sp() {
          padding: 6px 10px;
          letter-spacing: 2.1px;
        }
      }

      /* 前置きテキスト */
      .preface-text {
        margin-block-start: 1em;
        margin-block-end: 1em;
        font-size: 0.875rem;
        padding: 6px 0;
        line-height: 2;
        @include m.sp() {
          padding: 12px 0;
        }
        .indent {
          padding-left: 1rem;
        }
      }

      /* テキスト詳細 */
      .header-detail {
        text-align: start;
        list-style: none;
        position: relative;
        margin-left: -20px;
        margin-right: 20px;
        @include m.sp() {
          margin: 0 10px 0 -30px;
          letter-spacing: 2.1px;
        }

        .header-detail-text {
          font-size: 0.875rem;
          padding: 6px 0;
          line-height: 2;
          overflow-wrap: anywhere;
          @include m.sp() {
            padding: 12px 0;
          }
        }
        li:not(:last-child) {
          border-bottom: 1px solid v.$border-list-color;
        }

        .title {
          margin: 1rem 0;
        }

        /* 利用規約内に適用 */
        .text {

          ul, ol {
            list-style: none;
            padding-left: 0.8rem;
            margin: 1rem 0;

            &.indent {
              padding-left: 2rem;
            }
          }

          ul li, ol li {
            position: relative;
            margin: 0.3rem 0;
          }

          ul li {
            padding-left: 2.3em;
          }

          ul li::before {
            content: "";
            position: absolute;
            left: 0.7em;
            top: 1em;
            transform: none;
            width: 3px;
            height: 3px;
            background-color: currentColor;
            border-radius: 50%;
          }

          ol {
            counter-reset: list-counter;
          }

          ol li {
            padding-left: 2.3em;
          }

          ol li::before {
            position: absolute;
            width: auto;
            height: auto;
            background: none;
            border-radius: 0;
            transform: none;
            counter-increment: list-counter;
            content: counter(list-counter) ".";
            left: 0.5em;
            top: 0;
          }

          li:not(:last-child) {
            border: none;
          }
        }
        .indent {
          padding-left: 1rem;
        }
      }

      /* 最終更新日テキスト */
      .last-updated-text {
        text-align: end;
        font-size: 0.875rem;
        color: v.$text-base-color;
        margin-top: 20px;
        border-top: 1px solid v.$border-list-color;
        padding-top: 10px;
      }
    }
  }

  /* フッター */
  .modal-footer {
    background: v.$common-white-color;
    width: 100%;
    height: 112px;
    align-content: center;
    text-align: center;
    @include m.sp() {
      height: initial;
      bottom: 0;
      padding: 16px 0;
    }

    .button {
      width: 320px;
      height: 48px;
      transition: 0.3s all ease-out;
      cursor: pointer;
      border: 0;

      @include m.sp() {
        width: 164px;
        height: 48px;
      }

      .text {
        color: v.$common-white-color;
        font-size: 1rem;
        @include m.sp() {
          letter-spacing: 2.4px;
        }
      }
    }

    /* 閉じるボタン */
    .close-button {
      background: v.$common-element-color;
    }
    /* 送信するボタン */
    .accept-button {
      background: v.$accent-color;
      margin-left: 48px;
      @include m.sp() {
        margin-left: 15px;
      }
    }
    .close-button:hover {
      background: v.$common-hover-color;
    }
    .accept-button:hover {
      background: v.$accent-hover-color;
    }
  }
}
</style>
